/**
 * CourseList.js
 * Component for listing and managing courses
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Table, 
  Badge, 
  Alert,
  Spinner,
  Button,
  Form,
  InputGroup,
  Modal
} from 'react-bootstrap';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { courseService, commonService } from '../../services/api';

// Componente per la card con la lista dei corsi
const CourseCard = ({ 
  courses, 
  loading, 
  error, 
  pagination, 
  onPageChange, 
  onDelete,
  onEdit,
  onRefresh
}) => {
  if (loading) {
    return (
      <Card>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Caricamento corsi...</span>
          </Spinner>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="danger" className="mb-0">
            {error}
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5>Corsi ({pagination.totalCount})</h5>
          {onRefresh && (
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={onRefresh}
              disabled={loading}
              title="Ricarica lista corsi"
            >
              <i className="fas fa-sync-alt"></i>
            </Button>
          )}
        </div>

        {courses.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted">Nessun corso trovato con i filtri selezionati.</p>
          </div>
        ) : (
          <Table responsive striped hover>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Anno Sportivo</th>
                <th>Descrizione</th>
                <th>Gruppi</th>
                <th>Iscritti</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((courseData) => (
                <tr key={courseData.course.id}>
                  <td>
                    <Link 
                      to={`/courses/${courseData.course.id}`}
                      className="text-decoration-none fw-bold"
                    >
                      {courseData.course.name}
                    </Link>
                  </td>
                  <td>
                    <Badge bg="info">
                      {courseData.course.academicYear}/{courseData.course.academicYear + 1}
                    </Badge>
                  </td>
                  <td>{courseData.course.description || '-'}</td>
                  <td>
                    <Link 
                      to={`/groups?courseId=${courseData.course.id}&academicYear=${courseData.course.academicYear}`}
                      className="text-primary text-decoration-none fw-medium d-inline-flex align-items-center gap-1"
                      title="Clicca per vedere i gruppi di questo corso"
                    >
                      <i className="fas fa-users me-1"></i>
                      <span>Gruppi</span>
                      <Badge bg={courseData.totalGroups > 0 ? "primary" : "secondary"} pill className="ms-1">
                        {courseData.totalGroups || 0}
                      </Badge>
                      <i className="fas fa-chevron-right ms-1 text-muted" style={{ fontSize: '0.7rem' }}></i>
                    </Link>
                  </td>
                  <td>
                    <Badge bg="success">
                      {courseData.totalActiveSubscriptions || 0} attivi
                    </Badge>
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="btn-action"
                        onClick={() => onEdit(courseData.course.id)}
                        title="Modifica corso"
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="btn-action"
                        onClick={() => onDelete(courseData.course)}
                        disabled={courseData.totalActiveSubscriptions > 0}
                        title={courseData.totalActiveSubscriptions > 0 ? "Impossibile eliminare: corso con iscrizioni attive" : "Elimina corso"}
                      >
                        <i className="fas fa-trash-alt"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {/* Paginazione */}
        {pagination.totalPages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => onPageChange(pagination.page - 1)}
              >
                Precedente
              </Button>
              
              <span className="align-self-center px-3">
                Pagina {pagination.page} di {pagination.totalPages}
              </span>
              
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => onPageChange(pagination.page + 1)}
              >
                Successiva
              </Button>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const CourseList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  // Cattura la query string iniziale (al mount) per inizializzare i filtri senza causare loop
  const initialLocationSearchRef = useRef(location.search);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [error, setError] = useState('');
  const [coursesError, setCoursesError] = useState('');
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 1,
    page: parseInt(new URLSearchParams(location.search).get('page') || '1', 10),
    limit: 20
  });
  
  // Filtri
  const [filters, setFilters] = useState({
    search: '',
    academicYear: null // Verrà impostato dal backend
  });

  // Stato separato per il campo di ricerca con debounce
  const [searchInput, setSearchInput] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  // Anno accademico corrente dal backend
  const [currentAcademicYear, setCurrentAcademicYear] = useState(null);

  // Modal per conferma eliminazione
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);

  // Carica anno accademico corrente
  const loadCurrentAcademicYear = useCallback(async () => {
    try {
      const response = await commonService.getCurrentAcademicYear();
      if (response.success) {
        setCurrentAcademicYear(response.data);
        
        // Leggi parametri dalla query string solo al primo caricamento (evita loop con setSearchParams)
        const initialSearchParams = new URLSearchParams(initialLocationSearchRef.current);
        const urlSearch = initialSearchParams.get('search') || '';
        const urlAcademicYear = initialSearchParams.get('academicYear');
        
        // Applica filtri dalla query string
        setFilters(prev => ({
          ...prev,
          search: urlSearch,
          academicYear: urlAcademicYear ? parseInt(urlAcademicYear) : response.data
        }));
        setSearchInput(urlSearch);
      }
    } catch (error) {
      console.error('Error loading current academic year:', error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carica lista corsi
  const loadCourses = useCallback(async () => {
    try {
      setCoursesLoading(true);
      setCoursesError('');

      const params = {
        search: filters.search || null,
        academicYear: filters.academicYear || null,
        page: pagination.page,
        limit: pagination.limit
      };

      const response = await courseService.getCourses(params);
      
      if (response.success) {
        setCourses(response.data.items || []);
        setPagination(prev => ({
          ...prev,
          totalCount: response.data.totalCount || 0,
          totalPages: response.data.totalPages || 1,
          page: response.data.page || 1,
          limit: response.data.limit || 20
        }));
      } else {
        setCoursesError(response.message || 'Errore nel caricamento dei corsi');
      }
    } catch (err) {
      setCoursesError('Errore nel caricamento dei corsi');
      console.error('Error loading courses:', err);
    } finally {
      setCoursesLoading(false);
    }
  },[filters.search, filters.academicYear, pagination.page, pagination.limit]);

  // Caricamento iniziale e quando cambiano i filtri/paginazione
  useEffect(() => {
    // Carica corsi solo se currentAcademicYear è stato inizializzato
    if (currentAcademicYear !== null) {
      loadCourses();
      // Disabilita il loading generale una volta che i dati iniziali sono stati caricati
      if (loading) {
        setLoading(false);
      }
    }
  }, [loadCourses, currentAcademicYear, loading]);

  // Carica anno accademico corrente all'avvio
  useEffect(() => {
    loadCurrentAcademicYear();
  }, [loadCurrentAcademicYear]);

  // Sincronizza pagina e filtri nell'URL per ripristino al back
  useEffect(() => {
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.academicYear) params.academicYear = String(filters.academicYear);
    if (pagination.page && pagination.page !== 1) params.page = String(pagination.page);
    setSearchParams(params, { replace: true });
  }, [pagination.page, filters.search, filters.academicYear, setSearchParams]);

  // Debounce per il filtro di ricerca
  useEffect(() => {
    setSearchLoading(true);
    const timeoutId = setTimeout(() => {
      setFilters(prev => ({
        ...prev,
        search: searchInput
      }));
      setSearchLoading(false);
    }, 500); // 500ms di debounce

    return () => {
      clearTimeout(timeoutId);
      setSearchLoading(false);
    };
  }, [searchInput]);

  // Gestione filtri
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset alla prima pagina
  };

  // Gestione paginazione
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Gestione eliminazione corso
  const handleDeleteCourse = async () => {
    if (!courseToDelete) return;

    try {
      const response = await courseService.deleteCourse(courseToDelete.id);
      
      if (response.success) {
        setShowDeleteModal(false);
        setCourseToDelete(null);
        // Ricarica solo i dati della CourseCard
        loadCourses();
      } else {
        setCoursesError(response.message || 'Errore nell\'eliminazione del corso');
        setShowDeleteModal(false);
      }
    } catch (err) {
      setCoursesError('Errore nell\'eliminazione del corso');
      setShowDeleteModal(false);
      console.error('Error deleting course:', err);
    }
  };

  // Conferma eliminazione
  const confirmDelete = (course) => {
    setCourseToDelete(course);
    setShowDeleteModal(true);
  };

  // Callback per CourseCard
  const handleEditCourse = useCallback((courseId) => {
    navigate(`/courses/${courseId}`);
  }, [navigate]);

  const handleRefreshCourses = useCallback(() => {
    loadCourses();
  }, [loadCourses]);

  // Get academic year options (current and next 2 years)
  const getAcademicYearOptions = () => {
    const baseYear = currentAcademicYear || new Date().getFullYear();
    const years = [];
    for (let i = -2; i <= 2; i++) {
      const year = baseYear + i;
      years.push(year);
    }
    return years;
  };

  if (loading) {
    return (
      <div className="loading-spinner">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="course-list fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Gestione Corsi</h2>
        <Button 
          variant="primary" 
          onClick={() => navigate('/courses/new')}
        >
          + Nuovo Corso
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {/* Filtri */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Cerca per nome</Form.Label>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Nome corso..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  {searchLoading && (
                    <InputGroup.Text>
                      <Spinner animation="border" size="sm" />
                    </InputGroup.Text>
                  )}
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Anno Sportivo</Form.Label>
                <Form.Select
                  value={filters.academicYear || ''}
                  onChange={(e) => handleFilterChange('academicYear', e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">Tutti gli anni</option>
                  {getAcademicYearOptions().map(year => (
                    <option key={year} value={year}>
                      {year}/{year + 1}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Lista Corsi */}
      <CourseCard
        courses={courses}
        loading={coursesLoading}
        error={coursesError}
        pagination={pagination}
        onPageChange={handlePageChange}
        onDelete={confirmDelete}
        onEdit={handleEditCourse}
        onRefresh={handleRefreshCourses}
      />

      {/* Modal Conferma Eliminazione */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Conferma Eliminazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Sei sicuro di voler eliminare il corso <strong>{courseToDelete?.name}</strong>?
          <br />
          <small className="text-muted">
            Questa azione non può essere annullata.
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Annulla
          </Button>
          <Button variant="danger" onClick={handleDeleteCourse}>
            Elimina
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CourseList;